import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { Sidebar } from './components/layout/Sidebar';
import { TopBar } from './components/layout/TopBar';
import { getAgentConfig, getMe } from './lib/api';
import OnboardingWizard from './components/OnboardingWizard';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import AgentConfig from './pages/AgentConfig';
import AgentRoles from './pages/AgentRoles';
import Contractors from './pages/Contractors';
import ContractorDetail from './pages/ContractorDetail';
import ContractorResearch from './pages/ContractorResearch';
import VisualMatcher from './pages/VisualMatcher';
import VideoStudio from './pages/VideoStudio';
import Negotiations from './pages/Negotiations';
import NegotiationChat from './pages/NegotiationChat';
import Reports from './pages/Reports';
import EmailDrafts from './pages/EmailDrafts';
import PhoneCalls from './pages/PhoneCalls';
import Settings from './pages/Settings';
import Products from './pages/Products';
import Documents from './pages/Documents';
import Pipeline from './pages/Pipeline';
import StrategyPlanner from './pages/StrategyPlanner';
import MerchantPipeline from './pages/MerchantPipeline';
import ProjectAnalyzer from './pages/ProjectAnalyzer';
import LeadQueue from './pages/LeadQueue';
import OfferCreator from './pages/OfferCreator';
import Intelligence from './pages/Intelligence';
import Tenders from './pages/Tenders';
import TenderDetail from './pages/TenderDetail';
import ProfitRoom from './pages/ProfitRoom';
import PreTender from './pages/PreTender';
import Feed from './pages/Feed';
import Subscriptions from './pages/Subscriptions';
import Architects from './pages/Architects';
import HomeV3 from './pages/HomeV3';
import Proposals from './pages/Proposals';
import Meetings from './pages/Meetings';
import GenomeDashboard from './pages/GenomeDashboard';
import LightingAdvisor from './pages/LightingAdvisor';
import Catalogs from './pages/Catalogs';
import DialuxProjects from './pages/DialuxProjects';
import Suppliers from './pages/Suppliers';
import Investments from './pages/Investments';
import Automations from './pages/Automations';
import MyDashboard from './pages/MyDashboard';
import Glossary from './pages/Glossary';
import MyReports from './pages/MyReports';
import ReportIssueButton from './components/common/ReportIssueButton';
import CommandPalette from './components/common/CommandPalette';
import InteriorDesigners from './pages/InteriorDesigners';
import IcpEngine from './pages/IcpEngine';
import ResetPassword from './pages/ResetPassword';
import OnboardingPersonaWizard from './pages/OnboardingPersonaWizard';
import { initDiagnostics } from './lib/diagnostics';
import { blockForHandlowiec } from './components/common/RoleGate';
import InvestmentDetail from './pages/InvestmentDetail';
import Plans from './pages/Plans';
import AdminPanel from './pages/AdminPanel';
import Onboarding from './pages/Onboarding';
import Inbox from './pages/Inbox';
import WelcomeModal from './components/tutorial/WelcomeModal';
import MissionBoard from './components/tutorial/MissionBoard';
import GrandfatherBanner from './components/common/GrandfatherBanner';
import { api } from './lib/api';
import './App.css';

// Route transition wrapper — fade+slide na zmianie ścieżki (premium feel, 2026-05-30)
// Renderowany pod BrowserRouter → useLocation działa. key=pathname re-mountuje
// z animacją .page-enter (CSS, respektuje prefers-reduced-motion).
function RouteTransition({ children }) {
  const location = useLocation();
  return (
    <div key={location.pathname} className="page-enter">
      {children}
    </div>
  );
}

function App() {
  const [lang, setLang] = useState('pl');
  const [tenantId, setTenantId] = useState(null);
  const [agentName, setAgentName] = useState('Adam');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [agentConfig, setAgentConfig] = useState(null);

  // Tutorial state — Welcome Modal + Mission Board
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [tutorialPersona, setTutorialPersona] = useState('bidder');

  // Diagnostyka — instaluj globalne łapacze błędów raz (auto-kontekst zgłoszeń)
  useEffect(() => { initDiagnostics(); }, []);

  // Check tutorial state after auth
  useEffect(() => {
    if (!isAuthenticated || !tenantId) return;
    // 2026-05-27 (T6/E): persona → tutorial_persona mapping
    // seller_led → bidder (default content WelcomeModal)
    // designer   → investor (najbliższe — ROI/projektowanie/multi-industry)
    // Producer pozostaje jako legacy (jeśli ktoś go ustawi explicit)
    const userPersona = user?.preferences?.persona;
    if (userPersona === 'designer') {
      setTutorialPersona('investor');
    } else if (userPersona === 'seller_led') {
      setTutorialPersona('bidder');
    }
    api.get('/tutorial/state')
      .then(r => {
        const st = r.data;
        if (!st.welcome_seen && !st.tutorial_dismissed) {
          // Auto-show 1.5s after login
          setTimeout(() => setShowWelcomeModal(true), 1500);
        }
      })
      .catch(() => null);  // Graceful — endpoint may not exist on older versions
  }, [isAuthenticated, tenantId, user]);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (tenantId) {
      loadAgentConfig();
    }
  }, [tenantId]);

  async function checkAuth() {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        setTenantId(userData.tenant_id);
        setIsAuthenticated(true);
        if (userData.language) setLang(userData.language);

        // Verify token is still valid
        try {
          const freshUser = await getMe();
          setUser(freshUser);
          setTenantId(freshUser.tenant_id);
        } catch (err) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (err) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setReady(true);
  }

  function handleAuth(result) {
    setUser(result.user);
    setTenantId(result.user.tenant_id);
    setIsAuthenticated(true);
    if (result.user.language) setLang(result.user.language);
  }

  function handleLogout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setTenantId(null);
    setIsAuthenticated(false);
  }

  async function loadAgentConfig() {
    try {
      const config = await getAgentConfig(tenantId);
      setAgentConfig(config);
      if (config?.identity?.name) {
        setAgentName(config.identity.name);
      }
      // 2026-05-27 (T5/A z planu onboarding-simplification):
      // OnboardingWizard 7-step NIE jest już forced. Zostaje dostępny przez
      // Settings → "Skonfiguruj agenta szczegółowo" dla power-userów którzy
      // chcą detail. Defaults z backend invite_user/init są sensible
      // (T6/E pokrywa).
      // Stary kod:
      //   if (!config?.identity?.onboarded) { setShowOnboarding(true); }
      // — wyłączony świadomie.
    } catch (err) {
      console.error(err);
    }
  }

  function handleOnboardingComplete() {
    setShowOnboarding(false);
    loadAgentConfig();
  }

  if (!ready) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center mx-auto mb-3 animate-pulse">
            <svg className="w-5 h-5 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <p className="text-sm text-muted-foreground">Inicjalizacja...</p>
        </div>
      </div>
    );
  }

  // Reset-password page — dostępna BEZ logowania (zablokowani userzy)
  if (window.location.pathname === '/reset-password') {
    return (
      <>
        <ResetPassword />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  // Show auth page if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <AuthPage onAuth={handleAuth} />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  // T4 (2026-05-27): Persona wizard — force-redirect dla nowych userów + Izy
  // (P1 follow-up). Renderuje fullscreen przed Sidebar/TopBar gdy
  // user.preferences.persona == null.
  const personaNotSet = isAuthenticated && user && !user?.preferences?.persona;
  if (personaNotSet) {
    return (
      <BrowserRouter>
        <OnboardingPersonaWizard
          user={user}
          onPersonaSet={(result) => {
            // Aktualizuj user state lokalnie żeby wyjść z wizard view
            setUser((u) => ({
              ...u,
              preferences: {
                ...(u?.preferences || {}),
                persona: result.persona,
                persona_set_at: result.set_at,
                persona_set_by: 'self',
              },
            }));
            // localStorage też — żeby refresh nie wrzucił z powrotem do wizard
            try {
              const stored = JSON.parse(localStorage.getItem('user') || '{}');
              stored.preferences = {
                ...(stored.preferences || {}),
                persona: result.persona,
                persona_set_at: result.set_at,
                persona_set_by: 'self',
              };
              localStorage.setItem('user', JSON.stringify(stored));
            } catch {}
          }}
        />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-background text-foreground">
        {/* Onboarding Wizard (legacy, 1-step persona/industry/tenant info wizard) */}
        {showOnboarding && agentConfig && (
          <OnboardingWizard
            lang={lang}
            tenantId={tenantId}
            config={agentConfig}
            onComplete={handleOnboardingComplete}
          />
        )}

        {/* Tutorial Welcome Modal — 7-step per-persona intro */}
        <WelcomeModal
          visible={showWelcomeModal}
          persona={tutorialPersona}
          userName={user?.name}
          lang={lang}
          onComplete={() => setShowWelcomeModal(false)}
          onSkip={async () => {
            setShowWelcomeModal(false);
            await api.post('/tutorial/welcome-seen').catch(() => null);
          }}
        />

        {/* Mission Board — sticky widget, conditional via /tutorial/state */}
        <MissionBoard lang={lang} />

        <Sidebar
          lang={lang}
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <div className={`transition-all duration-200 ${sidebarCollapsed ? 'ml-[60px]' : 'ml-[220px]'}`}>
          <TopBar
            lang={lang}
            onLangChange={setLang}
            agentName={agentName}
            userName={user?.name}
            tenantId={tenantId}
            onLogout={handleLogout}
          />
          {isAuthenticated && <GrandfatherBanner lang={lang} />}
          <main className="p-6 max-w-[1440px]">
            <RouteTransition>
            <Routes>
              <Route path="/" element={
                tenantId && tenantId.startsWith('generic-tender')
                  ? <Navigate to="/tenders" replace />
                  : <Navigate to="/home-v3" replace />
              } />
              <Route path="/dashboard" element={blockForHandlowiec(<Dashboard lang={lang} tenantId={tenantId} />)} />
              <Route path="/genome" element={blockForHandlowiec(<GenomeDashboard lang={lang} tenantId={tenantId} />)} />
              <Route path="/agent-config" element={blockForHandlowiec(<AgentConfig lang={lang} tenantId={tenantId} />)} />
              <Route path="/agent-roles" element={blockForHandlowiec(<AgentRoles lang={lang} tenantId={tenantId} />)} />
              <Route path="/contractors" element={blockForHandlowiec(<Contractors lang={lang} tenantId={tenantId} />)} />
              <Route path="/contractors/:id" element={blockForHandlowiec(<ContractorDetail lang={lang} tenantId={tenantId} />)} />
              <Route path="/research" element={blockForHandlowiec(<ContractorResearch lang={lang} tenantId={tenantId} />)} />
              <Route path="/products" element={<Products lang={lang} tenantId={tenantId} />} />
              <Route path="/visual-match" element={blockForHandlowiec(<VisualMatcher lang={lang} tenantId={tenantId} />)} />
              <Route path="/documents" element={blockForHandlowiec(<Documents lang={lang} tenantId={tenantId} />)} />
              <Route path="/negotiations" element={blockForHandlowiec(<Negotiations lang={lang} tenantId={tenantId} />)} />
              <Route path="/negotiations/:id" element={blockForHandlowiec(<NegotiationChat lang={lang} tenantId={tenantId} />)} />
              <Route path="/pipeline" element={blockForHandlowiec(<Pipeline lang={lang} tenantId={tenantId} />)} />
              <Route path="/offers" element={blockForHandlowiec(<OfferCreator lang={lang} tenantId={tenantId} />)} />
              <Route path="/strategy" element={blockForHandlowiec(<StrategyPlanner lang={lang} tenantId={tenantId} />)} />
              <Route path="/merchant" element={blockForHandlowiec(<MerchantPipeline lang={lang} tenantId={tenantId} />)} />
              <Route path="/project-analyzer" element={blockForHandlowiec(<ProjectAnalyzer lang={lang} tenantId={tenantId} />)} />
              <Route path="/leads" element={blockForHandlowiec(<LeadQueue lang={lang} tenantId={tenantId} />)} />
              <Route path="/intelligence" element={blockForHandlowiec(<Intelligence lang={lang} tenantId={tenantId} />)} />
              <Route path="/tenders" element={<Tenders lang={lang} tenantId={tenantId} />} />
              <Route path="/tenders/:id" element={<TenderDetail lang={lang} tenantId={tenantId} />} />
              <Route path="/tenders/:id/profit-room" element={<ProfitRoom lang={lang} tenantId={tenantId} />} />
              <Route path="/video-studio" element={<VideoStudio lang={lang} tenantId={tenantId} />} />
              <Route path="/pre-tender" element={<PreTender lang={lang} tenantId={tenantId} />} />
              <Route path="/investments" element={<Investments lang={lang} tenantId={tenantId} />} />
              <Route path="/investments/:id" element={<InvestmentDetail lang={lang} tenantId={tenantId} />} />
              <Route path="/automations" element={blockForHandlowiec(<Automations lang={lang} tenantId={tenantId} />)} />
              <Route path="/my-dashboard" element={<MyDashboard lang={lang} tenantId={tenantId} />} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/zgloszenia" element={<MyReports />} />
              <Route path="/plans" element={blockForHandlowiec(<Plans lang={lang} tenantId={tenantId} />)} />
              <Route path="/feed" element={<Feed lang={lang} tenantId={tenantId} />} />
              <Route path="/subscriptions" element={blockForHandlowiec(<Subscriptions lang={lang} tenantId={tenantId} />)} />
              <Route path="/architects" element={<Architects lang={lang} tenantId={tenantId} />} />
              <Route path="/interior-designers" element={<InteriorDesigners lang={lang} tenantId={tenantId} />} />
              <Route path="/icp" element={<IcpEngine lang={lang} tenantId={tenantId} />} />
              <Route path="/home-v3" element={<HomeV3 lang={lang} tenantId={tenantId} />} />
              <Route path="/proposals" element={blockForHandlowiec(<Proposals lang={lang} tenantId={tenantId} />)} />
              <Route path="/meetings" element={blockForHandlowiec(<Meetings lang={lang} tenantId={tenantId} />)} />
              <Route path="/reports" element={blockForHandlowiec(<Reports lang={lang} tenantId={tenantId} />)} />
              <Route path="/emails" element={<EmailDrafts lang={lang} tenantId={tenantId} />} />
              <Route path="/phone-calls" element={blockForHandlowiec(<PhoneCalls lang={lang} tenantId={tenantId} />)} />
              <Route path="/lighting-advisor" element={<LightingAdvisor lang={lang} tenantId={tenantId} />} />
              <Route path="/catalogs" element={<Catalogs lang={lang} tenantId={tenantId} />} />
              <Route path="/dialux" element={blockForHandlowiec(<DialuxProjects lang={lang} tenantId={tenantId} />)} />
              <Route path="/suppliers" element={<Suppliers lang={lang} tenantId={tenantId} />} />
              <Route path="/admin" element={blockForHandlowiec(<AdminPanel lang={lang} tenantId={tenantId} />)} />
              <Route path="/onboarding" element={<Onboarding lang={lang} tenantId={tenantId} />} />
              <Route path="/inbox" element={<Inbox lang={lang} tenantId={tenantId} />} />
              <Route path="/settings" element={
                <Settings lang={lang} tenantId={tenantId} user={user} onLogout={handleLogout} />
              } />
            </Routes>
            </RouteTransition>
          </main>
        </div>
        {isAuthenticated && <ReportIssueButton />}
        {isAuthenticated && (
          <CommandPalette user={user} onLogout={handleLogout} lang={lang} />
        )}
        <Toaster position="top-right" richColors />
      </div>
    </BrowserRouter>
  );
}

export default App;
